import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEOMetadata = ({
  title,
  description,
  keywords,
  imageUrl,
  url,
  type = 'website',
  opportunity,
  effectiveCountry,
  effectiveUniversity,
  totalPositions
}) => {
  // Default values with improved SEO focus
  const defaultImage = 'https://phdscanner.com/phdscanner_logo.png';
  const defaultTitle = `${effectiveUniversity ? `PhD Positions at ${effectiveUniversity}` : 'Find Your Ideal PhD Position'}${effectiveCountry ? ` in ${effectiveCountry}` : ''} | PhDScanner`;
  const defaultDescription = `Discover ${totalPositions || ''}${effectiveUniversity ? ` ${effectiveUniversity}` : ''} PhD positions${effectiveCountry ? ` in ${effectiveCountry}` : ''}. Explore doctoral programs and fully funded opportunities.`;
  const defaultKeywords = `PhD positions${effectiveCountry ? ` in ${effectiveCountry}` : ''}${effectiveUniversity ? ` at ${effectiveUniversity}` : ''}, doctoral programs, fully funded PhD, ${effectiveCountry || 'global'} universities, Scholarship abroad, research opportunities, phd jobs, jobs`;

  // Construct dynamic metadata with fallbacks
  const seoTitle = title || defaultTitle;
  const seoDescription = description || defaultDescription;
  const seoImage = imageUrl || defaultImage;
  const seoUrl = url || 'https://www.phdscanner.com';
  const seoKeywords = keywords || defaultKeywords;

  const today = new Date();
  const oneMonthLater = new Date();
  oneMonthLater.setMonth(today.getMonth() + 1);


  // Organization structured data
  const organizationStructuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "PhDScanner",
    "description": "Connecting researchers with PhD opportunities worldwide",
    "url": seoUrl,
    "logo": defaultImage,
    "sameAs": [
      "https://www.linkedin.com/company/phdscanner",
      "https://twitter.com/phdscanner",
      "https://www.facebook.com/phdscanner"
    ]
  };

// Create generic JobPosting structured data for job listing pages
const createGenericJobPostingData = () => ({
  "@context": "https://schema.org",
  "@type": "JobPosting",
  "title": "PhD Research Opportunities",
  "description": "Discover PhD research opportunities worldwide through PhDScanner. Join leading research programs at top universities.",
  "datePosted": today.toISOString().split('T')[0],
  "validThrough": oneMonthLater.toISOString().split('T')[0],
  "employmentType": "FULL_TIME",
  "hiringOrganization": {
    "@type": "Organization",
    "name": "PhDScanner",
    "sameAs": "https://phdscanner.com",
    "logo": "https://phdscanner.com/logo.png"
  },
  "jobLocation": {
    "@type": "Place",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "University Campus",
      "addressLocality": "Worldwide",
      "addressRegion": "Global", 
      "postalCode": "00000",
      "addressCountry": "GB"
    },
    "name": "Global Universities"
  },
  "baseSalary": {
    "@type": "MonetaryAmount",
    "currency": "GBP",
    "value": {
      "@type": "QuantitativeValue",
      "value": 25000,
      "unitText": "YEAR"
    }
  },
  "experienceRequirements": "No prior experience required",
  "educationRequirements": "Master's degree in relevant field",
  "applicantLocationRequirements": {
    "@type": "Country",
    "name": "Worldwide"
  },
  "jobBenefits": "Research training, academic mentorship, conference opportunities",
  "industry": "Education",
  "jobLocationType": "TELECOMMUTE",
  "identifier": {
    "@type": "PropertyValue",
    "name": "PhDScanner ID",
    "value": `phd-listing-${Date.now()}`
  }
});

const jobPostingStructuredData = opportunity ? {
  "@context": "https://schema.org",
  "@type": "JobPosting",
  
  // Ensure title is never empty or null
  "title": opportunity.title || "PhD Research Opportunity",
  
  // Ensure description is never empty or null
  "description": opportunity.ai_summary || opportunity.description || "Explore PhD research opportunities worldwide through PhDScanner. Join leading research programs at top universities.",
  
  // Ensure datePosted is always present and valid
   "datePosted": opportunity.created_at 
    ? new Date(opportunity.created_at * 1000).toISOString().split('T')[0]
    : today.toISOString().split('T')[0],

    "validThrough": opportunity.closing_date 
    ? new Date(opportunity.closing_date * 1000).toISOString().split('T')[0]
    : oneMonthLater.toISOString().split('T')[0],
  
  "employmentType": "FULL_TIME",

  // Ensure hiringOrganization is never empty
  "hiringOrganization": {
    "@type": "Organization",
    "name": opportunity.university || effectiveUniversity || "PhDScanner",
    "sameAs": opportunity.university_url || "https://phdscanner.com",
    // Add logo for better rich results
    "logo": "https://phdscanner.com/logo.png" // Replace with your actual logo URL
  },

  // Ensure jobLocation and address are always complete
  "jobLocation": {
    "@type": "Place",
    "address": {
      "@type": "PostalAddress",
      // Ensure all address fields have fallback values
      "streetAddress":  "University Campus",
      "addressLocality": opportunity.city || "Not specified",
      "addressRegion": opportunity.country || "Not specified", 
      "postalCode": "00000",
      "addressCountry": opportunity.country || effectiveCountry || "GB"
    },
    // Add place name for clarity
    "name": `${opportunity.university || effectiveUniversity || "University"} - ${opportunity.city || "Campus"}`
  },

  "baseSalary": {
    "@type": "MonetaryAmount",
    "currency": "GBP",
    "value": {
      "@type": "QuantitativeValue",
      "value": 25000,
      "unitText": "YEAR"
    }
  },

  "experienceRequirements":  "No prior experience required",
  "educationRequirements":  "Master's degree in relevant field",
  
  // Add applicantLocationRequirements for Google Analytics SEO
  "applicantLocationRequirements": {
    "@type": "Country",
    "name": opportunity.country || effectiveCountry || "Worldwide"
  },
  
  // Add additional recommended fields
  "jobBenefits": "Research training, academic mentorship, conference opportunities",
  "industry": "Education",
  "jobLocationType": "TELECOMMUTE",
  
  // Add identifier for uniqueness
  "identifier": {
    "@type": "PropertyValue",
    "name": "PhDScanner ID",
    "value": opportunity.id || `phd-${Date.now()}`
  }
} : createGenericJobPostingData();

const courseStructuredData = {
  "@context": "https://schema.org",
  "@type": "Course",
  "name": opportunity?.title || "PhD Program in Research",
  "description": opportunity?.ai_summary || opportunity?.description || "Explore world-class PhD programs across global universities.",
  "provider": {
    "@type": "Organization",
    "name": opportunity?.university || effectiveUniversity || "PhDScanner",
    "sameAs": opportunity?.university_url || "https://phdscanner.com"
  },
  "hasCourseInstance": {
    "@type": "CourseInstance",
    "courseMode": "online",
    "instructor": {
      "@type": "Person",
      "name": opportunity?.supervisor 
    },
    "startDate": today.toISOString().split('T')[0],
    "endDate": oneMonthLater.toISOString().split('T')[0],
    "location": {
      "@type": "Place",
      "name": opportunity?.city || "Online"
    },
    "courseWorkload": "P3Y"
  },
  "offers": {
    "@type": "Offer",
    "url": seoUrl,
    "price": "0",
    "priceCurrency": "GBP",
    "availability": "https://schema.org/InStock",
    "validFrom": today.toISOString().split('T')[0],
    "category": "Education"
  }
};



  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={seoUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:site_name" content="PhDScanner" />
      <meta property="og:image" content={seoImage} />
      <meta property="og:image:secure_url" content={seoImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="PhDScanner Logo" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@phdscanner" />
      <meta name="twitter:creator" content="@phdscanner" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      <meta name="twitter:image:alt" content="PhDScanner Logo" />

      {/* LinkedIn */}
      <meta property="linkedin:title" content={seoTitle} />
      <meta property="linkedin:description" content={seoDescription} />
      <meta property="linkedin:image" content={seoImage} />
      <meta property="linkedin:url" content={seoUrl} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationStructuredData)}
      </script>
      
      {/* Always include JobPosting structured data */}
      <script type="application/ld+json">
        {JSON.stringify(jobPostingStructuredData)}
      </script>
      
      {opportunity && (
        <script type="application/ld+json">
          {JSON.stringify(courseStructuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOMetadata;